<template>
  <nav id="navigation" aria-label="Main" class="relative">
    <button
      class="relative flex-c focus-a z-1"
      :aria-label="isOpen ? 'Close' : 'Open'"
      aria-controls="main-navigation"
      :aria-expanded="isOpen"
      data-gtm-hamburger-menu
      @click="handleClick"
      @focus="handleFocus($event)"
    >
      <IconBase
        :icon="isOpen ? 'CloseIcon' : 'BurgerIcon'"
        color="primary"
        :size="mobile ? '2rem' : '2.2rem'"
      />
    </button>

    <Transition name="slide-left">
      <div
        v-show="isOpen"
        id="main-navigation"
        class="f-cover h-full bg-black-85"
      >
        <div
          class="h-full bg-white md:absolute md:y-inset md:right-0 md:w-144 md:bg-primary overflow-y-auto"
        >
          <div
            :style="`background-image: url(${image.src})`"
            class="w-full bg-full shadow-2xl"
          >
            <div class="md:w-96 md:pt-24">
              <div class="pt-12 pb-8 bg-white" @click="handleClick">
                <ul aria-label="Site navigation">
                  <li>
                    <Button
                      icon="ArrowIcon"
                      text="Home"
                      :path="{ name: 'home' }"
                      class="btn-lg btn-primary mb-2 md:mb-4 mx-auto"
                    />
                  </li>
                  <li class="">
                    <Button
                      icon="ArrowIcon"
                      text="Explore for yourself"
                      :path="{ name: 'campus' }"
                      class="btn-lg btn-primary mb-2 md:mb-4 mx-auto"
                    />
                  </li>
                  <li>
                    <Button
                      icon="ArrowIcon"
                      text="Take a guided tour"
                      :path="guidedTourPath"
                      class="btn-lg btn-primary mb-2 md:mb-4 mx-auto"
                    />
                  </li>
                </ul>

                <div class="flex-j">
                  <ul
                    aria-label="Social media links"
                    class="inline-block mt-2 mb-4"
                  >
                    <li
                      v-for="(item, key) in options.social_media"
                      v-show="item"
                      :key="key"
                      class="inline-block mx-2"
                    >
                      <Button
                        type="external"
                        :aria="key"
                        :path="item"
                        :icon="`${key}icon`"
                        iconsize="1.1rem"
                        @focus.native="handleFocus($event)"
                      />
                    </li>
                  </ul>
                </div>

                <ul aria-label="External links" class="text-center">
                  <li>
                    <Button
                      v-if="phone"
                      type="button"
                      icon="ArrowIcon"
                      text="Live Events"
                      class="btn-lg bg-primary text-white mx-auto mb-2"
                      @click.native="$store.commit('toggleLiveEventsState')"
                    />
                  </li>
                  <li v-for="(item, i) in ctas" :key="`${item.href}-${i}`">
                    <Button
                      type="external"
                      icon="LinkIcon"
                      iconsize="1rem"
                      :text="item.call_to_action"
                      textsize="lg"
                      :path="item.href"
                      class="btn-lg btn-secondary mx-auto mb-2"
                    />
                  </li>
                  <li class="mt-6 mb-6">
                    <a
                      href="options.accessibilty_statement"
                      target="blank"
                      class="block text-lg underline"
                      v-html="'Accessibility statement'"
                    />
                  </li>
                  <li>
                    <p>
                      Created by
                      <a
                        href="https://revolutionviewing.co.uk"
                        target="blank"
                        class="underline"
                        @focus="handleFocus($event)"
                        v-html="'RV'"
                      />
                    </p>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Transition>
  </nav>
</template>

<script>
import { mapState, mapGetters } from 'vuex'
import IconBase from '@/components/icons/IconBase.vue'
import Button from '@/components/buttons/Button.vue'

export default {
  name: 'Navigation',

  components: {
    IconBase,
    Button
  },

  data() {
    return {
      isOpen: false
    }
  },

  computed: {
    ...mapState(['touch', 'mobile', 'phone']),
    ...mapGetters(['options', 'categories', 'guidedtour']),

    image() {
      if (!this.phone) {
        return {
          src: this.options?.nav_image?.sizes?.large,
          alt: this.options?.nav_image?.alt
        }
      }
      return ''
    },

    guidedTourPath() {
      const firstStop = this.guidedtour?.children[0]

      if (firstStop) {
        return {
          name: 'guided-tour'
        }
      }

      return { name: 'home' }
    },

    ctas() {
      return this.options.calls_to_action.filter(
        item => item.href && item.call_to_action
      )
    }
  },

  methods: {
    handleClick() {
      this.isOpen = !this.isOpen
      this.$store.commit('toggleNavState')
    },

    handleFocus() {
      /*
       * Traps focus within the menu. Make sure to add the
       * focus handle on the first and last focusable elements
       */

      const domNode = document.getElementById('navigation')
      const focusableEls = [...domNode.querySelectorAll('button, a')]
      const firstFocusableEl = focusableEls[0]
      const lastFocusableEl = focusableEls[focusableEls.length - 1]

      event.target.addEventListener('keydown', e => {
        const isTabPressed = e.code === 'Tab'

        if (!isTabPressed) {
          return
        }

        if (e.shiftKey) {
          if (document.activeElement === firstFocusableEl && this.isOpen) {
            lastFocusableEl.focus()
            e.preventDefault()
          }
        } else {
          if (document.activeElement === lastFocusableEl) {
            firstFocusableEl.focus()
            e.preventDefault()
          }
        }
      })
    }
  }
}
</script>
